import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ApiError, apiRequest } from '../lib/api';

export interface AuthUser {
  id: number;
  email: string;
  display_name?: string | null;
  is_admin?: boolean | null;
  admin_role?: 'label' | 'artist' | null;
  has_admin_access?: boolean | null;
}

interface AuthSuccessResponse {
  token: string;
  user: AuthUser;
}

interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  initializing: boolean;
  signInWithPassword: (email: string, password: string, adminRole?: AuthUser['admin_role']) => Promise<void>;
  signUpWithPassword: (
    email: string,
    password: string,
    displayName?: string,
    adminRole?: AuthUser['admin_role'],
  ) => Promise<void>;
  sendOtp: (email: string) => Promise<void>;
  verifyOtp: (email: string, code: string, adminRole?: AuthUser['admin_role']) => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  resetPassword: (token: string, password: string, confirmPassword: string) => Promise<void>;
  signOut: () => void;
  refresh: () => Promise<void>;
  completeExternalAuth: (payload: AuthSuccessResponse) => Promise<void>;
  hydrateFromBootstrap: (payload: { user: AuthUser | null; token?: string | null }) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const AUTH_STORAGE_KEY = '254tracks.auth';

function loadStoredAuth(): AuthSuccessResponse | null {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.token) return null;
    return parsed as AuthSuccessResponse;
  } catch {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [initializing, setInitializing] = useState(true);
  const hasHydrated = useRef(false);
  const refreshInFlightRef = useRef<{ token: string; promise: Promise<void> } | null>(null);

  const persistAuth = useCallback((payload: AuthSuccessResponse | null) => {
    if (payload?.token && payload.user) {
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(payload));
      setToken(payload.token);
      setUser(payload.user);
    } else {
      localStorage.removeItem(AUTH_STORAGE_KEY);
      setToken(null);
      setUser(null);
    }
  }, []);

  const refresh = useCallback(
    async (overrideToken?: string) => {
      const activeToken = overrideToken ?? token;
      if (!activeToken) {
        persistAuth(null);
        setInitializing(false);
        return;
      }

       if (refreshInFlightRef.current?.token === activeToken) {
         return refreshInFlightRef.current.promise;
       }

       const refreshPromise = (async () => {
      try {
        const data = await apiRequest<{ user: AuthUser }>('/me', {
          authToken: activeToken,
        });
        persistAuth({ token: activeToken, user: data.user });
      } catch (error) {
        persistAuth(null);
        if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Unable to refresh session', 0, null);
      } finally {
        setInitializing(false);
      }
       })();

       refreshInFlightRef.current = { token: activeToken, promise: refreshPromise };
       try {
         await refreshPromise;
       } finally {
         if (refreshInFlightRef.current?.token === activeToken) {
           refreshInFlightRef.current = null;
         }
       }
    },
    [persistAuth, token],
  );

  useEffect(() => {
    if (hasHydrated.current) return;
    hasHydrated.current = true;

    const skipInitialRefresh =
      typeof window !== 'undefined' &&
      (((window as { __HOME_BOOTSTRAP__?: boolean }).__HOME_BOOTSTRAP__ === true) ||
        ((window as { __ARTISTS_BOOTSTRAP__?: boolean }).__ARTISTS_BOOTSTRAP__ === true) ||
        window.location.pathname.endsWith('/artists') ||
        window.location.pathname.startsWith('/artists/') ||
        window.location.pathname.startsWith('/explore'));
    const stored = loadStoredAuth();
    if (stored?.token) {
      persistAuth(stored);
      if (!skipInitialRefresh) {
        refresh(stored.token).catch(() => undefined);
      } else {
        setInitializing(false);
      }
    } else {
      setInitializing(false);
    }
  }, [persistAuth, refresh]);

  const persistAndHydrate = useCallback(
    async (payload: AuthSuccessResponse) => {
      persistAuth(payload);
      try {
        await refresh(payload.token);
      } catch {
        // keep the session active even if the metadata fetch fails
      }
    },
    [persistAuth, refresh],
  );

  const signInWithPassword = useCallback(
    async (email: string, password: string, adminRole?: AuthUser['admin_role']) => {
      const data = await apiRequest<AuthSuccessResponse>('/auth/signin', {
        method: 'POST',
        body: JSON.stringify({ email, password, admin_role: adminRole }),
      });
      await persistAndHydrate(data);
    },
    [persistAndHydrate],
  );

  const signUpWithPassword = useCallback(
    async (
      email: string,
      password: string,
      displayName?: string,
      adminRole?: AuthUser['admin_role'],
    ) => {
      const data = await apiRequest<AuthSuccessResponse>('/auth/signup', {
        method: 'POST',
        body: JSON.stringify({
          email,
          password,
          display_name: displayName,
          admin_role: adminRole,
        }),
      });
      await persistAndHydrate(data);
    },
    [persistAndHydrate],
  );

  const sendOtp = useCallback(async (email: string) => {
    await apiRequest('/auth/otp/send', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }, []);

  const verifyOtp = useCallback(
    async (email: string, code: string, adminRole?: AuthUser['admin_role']) => {
      const data = await apiRequest<AuthSuccessResponse>('/auth/otp/verify', {
        method: 'POST',
        body: JSON.stringify({ email, code, admin_role: adminRole }),
      });
      await persistAndHydrate(data);
    },
    [persistAndHydrate],
  );

  const requestPasswordReset = useCallback(async (email: string) => {
    await apiRequest('/auth/password/forgot', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }, []);

  const resetPassword = useCallback(
    async (resetToken: string, password: string, confirmPassword: string) => {
      await apiRequest('/auth/password/reset', {
        method: 'POST',
        body: JSON.stringify({
          token: resetToken,
          password,
          confirm_password: confirmPassword,
        }),
      });
    },
    [],
  );

  const signOut = useCallback(() => {
    persistAuth(null);
  }, [persistAuth]);
  const completeExternalAuth = useCallback(
    async (payload: AuthSuccessResponse) => {
      await persistAndHydrate(payload);
    },
    [persistAndHydrate]
  );

  const hydrateFromBootstrap = useCallback(
    (payload: { user: AuthUser | null; token?: string | null }) => {
      const nextToken = payload.token ?? token;
      if (payload.user && nextToken) {
        persistAuth({ token: nextToken, user: payload.user });
      } else if (!payload.user) {
        persistAuth(null);
      }
      setInitializing(false);
    },
    [persistAuth, token]
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      token,
      initializing,
      signInWithPassword,
      signUpWithPassword,
      sendOtp,
      verifyOtp,
      requestPasswordReset,
      resetPassword,
      signOut,
      refresh: () => refresh(),
      completeExternalAuth,
      hydrateFromBootstrap,
    }),
    [
      initializing,
      refresh,
      requestPasswordReset,
      resetPassword,
      sendOtp,
      signUpWithPassword,
      signInWithPassword,
      signOut,
      token,
      user,
      verifyOtp,
      completeExternalAuth,
      hydrateFromBootstrap,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return ctx;
}
